import type { MouseEvent } from 'react';
import React, { useCallback, useState } from 'react';
import { defineMessages, useIntl } from 'react-intl-next';

import { Box, Text, Stack, Inline, Pressable } from '@atlaskit/primitives/compiled';
import { cssMap } from '@atlaskit/css';
import { token } from '@atlaskit/tokens';
import { Field } from '@atlaskit/form';
import SpeedometerLeftIcon from '@atlaskit/icon-lab/core/speedometer-left';
import SpeedometerUpIcon from '@atlaskit/icon-lab/core/speedometer-up';
import SpeedometerRightIcon from '@atlaskit/icon-lab/core/speedometer-right';

import { useAudioBriefing } from '../../../../hooks/useAudioBriefing';
import type { CustomLengthType } from '../../../../types/customizationTypes';
import { CustomLength } from '../../../../types/customizationTypes';

const i18n = defineMessages({
	audioDurationLabel: {
		id: 'audio.audio-briefing-popup.customize-tab.duration-label',
		defaultMessage: 'How long is the audio, approximately?',
		description: 'Label for a Select component used for selecting audio duration',
	},
	shortDurationLabel: {
		id: 'audio.audio-briefing-popup.customize-tab.duration.short',
		defaultMessage: '1 minute',
		description: 'Label for a 1 minute audio option',
	},
	mediumDurationLabel: {
		id: 'audio.audio-briefing-popup.customize-tab.duration.medium',
		defaultMessage: '3 minutes',
		description: 'Label for a 3 minute audio option',
	},
	longDurationLabel: {
		id: 'audio.audio-briefing-popup.customize-tab.duration.long',
		defaultMessage: '8 minutes',
		description: 'Label for a 8 minute audio option',
	},
});

interface DurationOption {
	label: string;
	icon: React.ElementType;
	value: CustomLengthType;
}

const styles = cssMap({
	selectedDurationOption: {
		flex: 1,
		backgroundColor: token('color.background.selected'),
		borderRadius: token('border.radius.100'),
		paddingBlockStart: token('space.100'),
		paddingBlockEnd: token('space.075'),
	},
	durationOption: {
		flex: 1,
		backgroundColor: token('elevation.surface'),
		paddingBlockStart: token('space.100'),
		paddingBlockEnd: token('space.075'),
		borderColor: token('color.border'),
		borderRadius: token('border.radius.100'),
		borderWidth: token('border.width'),
		borderStyle: 'solid',
	},
});

// Labels are purposely string values rather than Message objects
// This allows us to call FormattedMessage where needed
const durationOptions: DurationOption[] = [
	{
		label: 'shortDurationLabel',
		icon: SpeedometerLeftIcon,
		value: CustomLength.SHORT,
	},
	{
		label: 'mediumDurationLabel',
		icon: SpeedometerUpIcon,
		value: CustomLength.MEDIUM,
	},
	{
		label: 'longDurationLabel',
		icon: SpeedometerRightIcon,
		value: CustomLength.LONG,
	},
];

const DurationOptionComponent = ({
	value,
	setDuration,
	icon: Icon,
	changeHandler,
	option,
	isSelected = false,
}: {
	value: CustomLengthType;
	setDuration: (value: CustomLengthType) => void;
	icon: React.ElementType;
	changeHandler: Function;
	option: DurationOption;
	isSelected?: boolean;
}) => {
	const { formatMessage } = useIntl();

	const handleClick = useCallback(
		(e: MouseEvent<HTMLButtonElement>) => {
			e.preventDefault();
			setDuration(value);
			changeHandler(value);
		},
		[value, setDuration, changeHandler],
	);

	return (
		<Pressable
			xcss={isSelected ? styles.selectedDurationOption : styles.durationOption}
			onClick={handleClick}
		>
			<Stack>
				<Box paddingBlockEnd="space.075">
					<Icon color={token('color.icon')} label="" />
				</Box>
				<Text>{formatMessage(i18n[option.label])}</Text>
			</Stack>
		</Pressable>
	);
};

export const DurationSelector = () => {
	const { formatMessage } = useIntl();
	const [{ customization }] = useAudioBriefing();
	const { length } = customization;
	const [selectedDuration, setSelectedDuration] = useState(length);

	const handleSetDuration = useCallback((duration: CustomLengthType) => {
		setSelectedDuration(duration);
	}, []);

	return (
		<Field
			name="length"
			defaultValue={selectedDuration}
			label={formatMessage(i18n.audioDurationLabel)}
		>
			{({ fieldProps }) => (
				<Box data-name={fieldProps.id} data-value={fieldProps.value}>
					<Inline grow="fill" spread="space-between" space="space.100">
						{durationOptions.map((option) => {
							const isSelected = option.value === selectedDuration;
							return (
								<DurationOptionComponent
									key={option.value}
									value={option.value}
									setDuration={handleSetDuration}
									icon={option.icon}
									isSelected={isSelected}
									changeHandler={fieldProps.onChange}
									option={option}
								/>
							);
						})}
					</Inline>
				</Box>
			)}
		</Field>
	);
};
